import { z } from "zod";
import { CUSTOMER_SOURCES } from "../enums";
import type { Channel, ConversationStatus, CustomerSource } from "../enums";
import type { OrderListItemDto } from "./orders";

// Customers (CRM). Phones are stored in E.164 and unique per client; merged duplicates keep a pointer to the
// customer they were merged into.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const boolQuery = z.enum(["true", "false"]).transform((v) => v === "true");
const optionalEmail = z
  .union([z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(254), z.literal("")])
  .nullable()
  .optional();
const tags = z.array(z.string().trim().min(1).max(40)).max(30, "30 tags maximum");

export const CUSTOMER_SORTS = ["recent", "name", "orders", "spent", "last_order"] as const;
export const CUSTOMER_RISK_LEVELS = ["low", "medium", "high"] as const;

export const customerListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  tag: z.string().trim().max(40).optional(),
  source: z.enum(CUSTOMER_SOURCES).optional(),
  segment: z.string().trim().max(40).optional(),
  risk: z.enum(CUSTOMER_RISK_LEVELS).optional(),
  blocked: boolQuery.optional(),
  hasOrders: boolQuery.optional(),
  sort: z.enum(CUSTOMER_SORTS).default("recent"),
});
export type CustomerListQuery = z.infer<typeof customerListQuery>;
export type CustomerListQueryInput = z.input<typeof customerListQuery>;

const customerFields = {
  name: z.string().trim().min(2, "Nom requis").max(120),
  phone: z.string().trim().min(6, "Téléphone requis").max(40),
  altPhone: z.string().trim().max(40).nullable().optional(),
  email: optionalEmail,
  gender: z.enum(["male", "female", "unknown"]).optional(),
  language: z.string().trim().max(10).optional(),
  governorate: z.string().trim().max(80).optional(),
  delegation: z.string().trim().max(80).optional(),
  city: z.string().trim().max(80).optional(),
  address: z.string().trim().max(240).optional(),
  zip: z.string().trim().max(12).optional(),
  marketCode: z.string().trim().length(2).toUpperCase().optional(),
  tags: tags.optional(),
  marketingConsent: z.boolean().optional(),
};

export const customerCreateSchema = z.object({
  ...customerFields,
  source: z.enum(CUSTOMER_SOURCES).optional().default("manual"),
  note: z.string().trim().max(4000).optional(),
});
export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;

export const customerUpdateSchema = z.object({
  ...customerFields,
  phone: z.string().trim().min(6, "Téléphone invalide").max(40).nullable(),
}).partial();
export type CustomerUpdateInput = z.infer<typeof customerUpdateSchema>;

export const customerNoteInputSchema = z.object({
  text: z.string().trim().min(1, "Note vide").max(4000, "4 000 caractères maximum"),
});

export const customerTagsSchema = z.object({ tags });

export const customerBlockSchema = z.object({
  reason: z.string().trim().max(300).optional().default(""),
});

export const customerMergeSchema = z.object({
  /** Duplicate merged into the customer of the URL, then marked as merged. */
  mergeId: id,
});

export interface CustomerStatsDto {
  ordersCount: number;
  deliveredCount: number;
  returnedCount: number;
  cancelledCount: number;
  totalSpentMinor: number;
  avgOrderMinor: number;
  firstOrderAt: string | null;
  lastOrderAt: string | null;
  /** Returned / (delivered + returned), null without a finished delivery. */
  returnRate: number | null;
}

export interface CustomerDto {
  id: string;
  name: string;
  phone: string | null;
  phoneValid: boolean;
  altPhone: string | null;
  email: string | null;
  gender: "male" | "female" | "unknown";
  language: string;
  governorate: string;
  delegation: string;
  city: string;
  address: string;
  zip: string;
  country: string;
  marketCode: string;
  avatarUrl: string | null;
  tags: string[];
  source: CustomerSource;
  sourceDetail: string;
  channels: Channel[];
  channelIdentities: Array<{ channel: Channel; externalId: string; displayName: string; profilePicUrl: string | null }>;
  stats: CustomerStatsDto;
  rfm: { segment: string | null; clvMinor: number | null; nextPurchaseAt: string | null };
  risk: { score: number | null; level: "low" | "medium" | "high" | null; factors: string[] };
  blocked: boolean;
  blockedReason: string | null;
  marketingConsent: boolean;
  mergedInto: string | null;
  lastContactAt: string | null;
  currency: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerNoteDto {
  id: string;
  text: string;
  authorId: string | null;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerConversationDto {
  id: string;
  channel: Channel;
  status: ConversationStatus;
  lastMessageAt: string;
  lastMessagePreview: string;
  unreadCount: number;
}

export interface CustomerDetailDto {
  customer: CustomerDto;
  orders: OrderListItemDto[];
  conversations: CustomerConversationDto[];
  notes: CustomerNoteDto[];
  returnsCount: number;
}
